// dateText/parseDateText.js — reads a date range from the free date text of a document (plan
// bugfix_2026-10-12_chef-sammelliste, phase datum-text). Pure: text in, range out; the caller
// (legacy extractDate in documents.js) fills the from/to fields.
//
// Rules:
// - The first "[day] [month] year" in the text counts; year = four digits, month = number or name.
// - Month names come from dateTextLanguages, only the languages the caller passes (default: all); case, accents and
//   a trailing dot do not matter.
// - Separators: ".", ". ", " " and "/" ("12/3/1920" is 12 March, European order).
// - A single number before the year is the month ("3.1920" = March 1920).
// - A month that cannot be resolved (number outside 1–12, unknown word) → whole year, a day is dropped.
// - A day past the month end (29–31) is cut to the last day; a day above 31 → no range.
// - An approximation word right before the date, or taken by the pattern as month ("ungefähr 1920"), widens the
//   range by one unit: year ±1 year, month ±1 month, day ±1 day (boss 2026-10-13; was year ±5, day ±7). The to-date
//   is then cut at today.

import { DATE_TEXT_LANGUAGES } from './dateTextLanguages.js';

const PATTERN = /(?:(\d{1,2})(?:\. ?| |\/))?(?:(\d{1,2}|\p{L}{3,10})(?:\. ?| |\/))?(\d{4})/u;

// "Févr." → "fevr": lower case, accents and a trailing dot removed.
export function normalizeWord(word) {
    return word.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/\.$/, '');
}

// Normalized month name → month number over all languages. Throws when one word means different months — a
// config error that must not pass silently (the date text then stays unrecognised with an error in the console).
export function buildMonthLookup(languages) {
    const lookup = new Map();
    for (const [language, { months }] of Object.entries(languages)) {
        months.forEach((names, index) => {
            for (const name of names) {
                const word = normalizeWord(name);
                const known = lookup.get(word);
                if (known !== undefined && known !== index + 1) {
                    throw new Error(`dateTextLanguages: "${name}" (${language}) is month ${index + 1}, elsewhere month ${known}`);
                }
                lookup.set(word, index + 1);
            }
        });
    }
    return lookup;
}

const ALL_LANGUAGES = Object.keys(DATE_TEXT_LANGUAGES);

// One vocabulary { months, approximate } per language combination, built on first use.
const vocabularies = new Map();

// Language codes without an entry in dateTextLanguages are skipped on purpose: most menu languages have no words
// yet, the other passed languages still count.
function vocabularyFor(languages) {
    const known = [...new Set(languages)].filter((code) => code in DATE_TEXT_LANGUAGES).sort();
    const key = known.join(',');
    if (!vocabularies.has(key)) {
        const entries = known.map((code) => [code, DATE_TEXT_LANGUAGES[code]]);
        vocabularies.set(key, {
            months: buildMonthLookup(Object.fromEntries(entries)),
            approximate: new Set(entries.flatMap(([, entry]) => entry.approximate.map(normalizeWord))),
        });
    }
    return vocabularies.get(key);
}

// Month number 1–12, or null when the token is no usable month.
function resolveMonth(token, months) {
    if (/^\d+$/.test(token)) {
        const month = Number(token);
        return month >= 1 && month <= 12 ? month : null;
    }
    return months.get(normalizeWord(token)) ?? null;
}

// Date arithmetic in UTC with setUTCFullYear: no daylight saving shifts, and years below 100 stay as they are
// (new Date(year, …) would turn them into 19xx).
function utcDate(year, month, day) {
    const date = new Date(0);
    date.setUTCFullYear(year, month - 1, day);
    return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}

function daysInMonth(year, month) {
    return utcDate(year, month + 1, 0).day;
}

function isoDate({ year, month, day }) {
    return String(year).padStart(4, '0') + '-' + String(month).padStart(2, '0') + '-' + String(day).padStart(2, '0');
}

// What the text names: { year } | { year, month } | { year, month, day }, or null (day above 31).
function resolveDate(dayToken, monthToken, year, months) {
    // "3.1920": the only number before the year is the month.
    if (dayToken !== undefined && monthToken === undefined) {
        monthToken = dayToken;
        dayToken = undefined;
    }
    if (monthToken === undefined) return { year };

    const month = resolveMonth(monthToken, months);
    if (month === null) return { year };

    const day = dayToken === undefined ? 0 : Number(dayToken);
    if (day > 31) return null;
    if (day === 0) return { year, month };

    return { year, month, day: Math.min(day, daysInMonth(year, month)) };
}

function exactRange({ year, month, day }) {
    if (month === undefined) return { from: isoDate({ year, month: 1, day: 1 }), to: isoDate({ year, month: 12, day: 31 }) };
    if (day === undefined) return { from: isoDate({ year, month, day: 1 }), to: isoDate({ year, month, day: daysInMonth(year, month) }) };
    return { from: isoDate({ year, month, day }), to: isoDate({ year, month, day }) };
}

function approximateRange({ year, month, day }) {
    if (month === undefined) return { from: isoDate({ year: year - 1, month: 1, day: 1 }), to: isoDate({ year: year + 1, month: 12, day: 31 }) };
    if (day === undefined) {
        const first = utcDate(year, month - 1, 1);
        const last = utcDate(year, month + 1, 1);
        return { from: isoDate(first), to: isoDate({ ...last, day: daysInMonth(last.year, last.month) }) };
    }
    return { from: isoDate(utcDate(year, month, day - 1)), to: isoDate(utcDate(year, month, day + 1)) };
}

// The pattern is not bound to word starts: in a word longer than 10 letters it takes only the end
// ("approximately" → "roximately"). Returns the whole word and where it starts.
function wholeWord(text, token, start) {
    const lead = text.slice(0, start).match(/\p{L}*$/u)[0];
    return { word: lead + token, start: start - lead.length };
}

// Returns { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }, or null when nothing is found or the from-date is not in the
// past. languages = language codes whose words count (default: all in dateTextLanguages); `today` for tests.
export function parseDateText(text, { languages = ALL_LANGUAGES, today = new Date() } = {}) {
    const match = text.match(PATTERN);
    if (!match) return null;

    let [, dayToken, monthToken] = match;
    const year = Number(match[3]);
    let start = match.index;
    const { months, approximate } = vocabularyFor(languages);

    if (dayToken === undefined && monthToken !== undefined && !/^\d/.test(monthToken)) {
        ({ word: monthToken, start } = wholeWord(text, monthToken, start));
    }

    // "ungefähr 1920": the pattern took the approximation word as month.
    let isApproximate = monthToken !== undefined && approximate.has(normalizeWord(monthToken));
    if (isApproximate) {
        monthToken = undefined;
        dayToken = undefined;
    } else {
        // "ca. März 1920": the word right before the date.
        const before = text.slice(0, start).match(/(\p{L}+)\.?\s*$/u);
        isApproximate = before !== null && approximate.has(normalizeWord(before[1]));
    }

    const date = resolveDate(dayToken, monthToken, year, months);
    if (!date) return null;

    const range = isApproximate ? approximateRange(date) : exactRange(date);
    if (!(new Date(range.from) < today)) return null;

    if (isApproximate) {
        const todayIso = isoDate({ year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() });
        if (range.to > todayIso) range.to = todayIso;
    }
    return range;
}
